import { useQuery } from "@tanstack/react-query";
import { useOrganization } from "../contexts/OrganizationContext";
import { useModule } from "../contexts/ModuleContext";
import { searchCollection, buildContactsAwareFilter } from "../lib/typesense-service";
import { getSearchApiKey } from "../lib/search/typesenseScopedSearch";
import { isTypesenseEnabled } from "../config/typesense";
import { useDebounce } from "./useDebounce";
import type { SearchResult, SearchOptions } from "../types/search";

export const searchKeys = {
  all: ["typesense"] as const,
  collection: (name: string) => [...searchKeys.all, name] as const,
  search: (name: string, query: string, filters?: string) =>
    [...searchKeys.collection(name), query, filters] as const,
};

/**
 * useTypesenseSearch — search a single Typesense collection with debouncing.
 *
 * @param collection - Typesense collection name (e.g. "quotes", "contacts")
 * @param query - Raw search string from the user
 * @param options - Optional search parameters (filterBy, sortBy, perPage, etc.)
 * @returns Typed search results, loading, and error state
 *
 * @example
 * const { data, isLoading } = useTypesenseSearch<QuoteDocument>('quotes', searchQuery, {
 *   filterBy: 'status:=open',
 *   perPage: 10,
 * });
 */
export function useTypesenseSearch<T = Record<string, unknown>>(
  collection: string,
  query: string,
  options: Omit<SearchOptions, "collection"> = {},
): {
  data: SearchResult<T> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  /**
   * The query `data` is the answer FOR, or null while it is not one (still debouncing, fetching, or
   * showing the previous query's results as placeholder). A page that says "nothing matches" must
   * check this equals what is typed, or it judges the previous answer.
   */
  answeredQuery: string | null;
} {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;
  const { hasModuleAccess } = useModule();
  const minChars = options.minChars ?? 2;
  // This hook owns the debounce. Callers pass raw input — see SearchOptions.debounceMs.
  const debouncedQuery = useDebounce(query, options.debounceMs ?? 300);
  const typesenseEnabled = isTypesenseEnabled();

  // Reuses buildContactsAwareFilter (typesense-service.ts) rather than
  // duplicating org-scope logic, so this generic hook and searchCollection's
  // own callers cannot drift apart. That helper covers: `assets`'s `orgId`
  // field vs. every other collection's `organizationId`; the P14-gated
  // `isCandidate:=false` clause on `contacts` (#2559); and `active:=`true``
  // on `qbo_customers` so a QBO-deleted customer never surfaces in search
  // (#4084 — used by CustomerList's PageSearch). This filter is defense-in-
  // depth for org isolation — enforced by the scoped key from
  // getSearchApiKey — but keeping it correct avoids a silent zero-result
  // filter if this generic hook is ever pointed at `assets`.
  const orgAwareFilter = buildContactsAwareFilter(
    collection,
    orgId ?? "",
    hasModuleAccess("P14"),
  );
  const filterBy = options.filterBy
    ? `${orgAwareFilter} && ${options.filterBy}`
    : orgAwareFilter;

  const enabled =
    typesenseEnabled && !!orgId && debouncedQuery.length >= minChars;

  const result = useQuery({
    queryKey: searchKeys.search(collection, debouncedQuery, filterBy),
    queryFn: async () => {
      // Authenticate with the per-org scoped key (the broad key only for the
      // non-organizationId `assets` fallback). Org isolation is enforced by the
      // key itself; the client-side org filter above is now defense-in-depth.
      const apiKey = await getSearchApiKey(collection);
      return searchCollection<T>(collection, debouncedQuery, {
        apiKey,
        queryBy: options.queryBy,
        filterBy,
        sortBy: options.sortBy,
        perPage: options.perPage ?? 20,
        page: options.page ?? 1,
        facetBy: options.facetBy,
      });
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    answeredQuery:
      enabled && result.data !== undefined && !result.isPlaceholderData && !result.isFetching ? debouncedQuery : null,
  };
}
